import React, { useCallback, useEffect, useState } from 'react';
import { requestEditTree } from '../data/firestore';

// Human-readable group headers for the stage list.
const GROUP_LABEL = {
  base: 'Per-frame auto base',
  clip: 'Clip feedback',
  override: 'Per-proof override',
  crop: 'Reframe',
};
const GROUP_ORDER = ['base', 'clip', 'override', 'crop'];

const fmtNum = (v) => (typeof v === 'number' ? (Math.round(v * 100) / 100) : v);

// "white balance -8 → +6", "input transform: log3g10", "reframed".
function chipText(c) {
  if (c.key === 'inputTransform') return `input transform: ${c.to}`;
  if (c.key === 'crop') return 'reframed';
  if (c.from == null) return `${c.label} ${c.to > 0 ? '+' : ''}${fmtNum(c.to)}`;
  return `${c.label} ${fmtNum(c.from)} → ${fmtNum(c.to)}`;
}

// Big, full-screen side-by-side inspector: step through every cumulative stage,
// comparing the frame BEFORE vs AFTER that stage's adjustment so you can dial in
// exactly where the look goes right/wrong.
function StageInspector({ stages, index, setIndex, onClose, perception }) {
  const focused = stages[index] || null;
  const before = index > 0 ? stages[index - 1] : null;

  const step = useCallback((d) => {
    setIndex((i) => Math.max(0, Math.min(stages.length - 1, i + d)));
  }, [setIndex, stages.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, onClose]);

  if (!focused) return null;

  return (
    <div className="cs-treezoom" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="cs-treezoom-inner" onClick={(e) => e.stopPropagation()}>
        <div className="cs-treezoom-head">
          <div style={{ minWidth: 0 }}>
            <div className="cs-treezoom-title">{focused.label}</div>
            <div className="cs-help" style={{ fontSize: 12 }}>
              Stage {index + 1} / {stages.length} · {GROUP_LABEL[focused.group] || focused.group}
              {perception?.wbRef ? <> · WB ref <b>{perception.wbRef}</b></> : null}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => step(-1)} disabled={index === 0} title="Previous stage (←)">←</button>
            <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => step(1)} disabled={index === stages.length - 1} title="Next stage (→)">→</button>
            <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={onClose} title="Close (Esc)">✕</button>
          </div>
        </div>

        <div className={`cs-treezoom-compare ${before ? '' : 'single'}`}>
          {before && (
            <figure className="cs-treezoom-fig">
              <div className="cs-treezoom-imgwrap">
                <img src={before.url} alt={before.label} />
                <span className="cs-treezoom-badge">before</span>
              </div>
              <figcaption className="cs-help">{before.label}</figcaption>
            </figure>
          )}
          <figure className="cs-treezoom-fig">
            <div className="cs-treezoom-imgwrap cs-treezoom-after">
              <img src={focused.url} alt={focused.label} />
              <span className="cs-treezoom-badge cs-treezoom-badge-after">{before ? 'after' : 'starting point'}</span>
            </div>
            <figcaption className="cs-help">{focused.label}</figcaption>
          </figure>
        </div>

        {!!(focused.changed || []).length && (
          <div className="cs-aiasst-ops cs-treezoom-chips">
            {focused.changed.map((c, k) => <span key={k} className="cs-aiasst-op">{chipText(c)}</span>)}
          </div>
        )}
        {!(focused.changed || []).length && (
          <div className="cs-help cs-treezoom-chips" style={{ fontSize: 12 }}>No parameter change at this stage — it carries the previous result forward.</div>
        )}

        {/* Filmstrip — jump to any stage. */}
        <div className="cs-treezoom-strip">
          {stages.map((s, i) => (
            <button
              key={s.id}
              type="button"
              className={`cs-treezoom-cell ${i === index ? 'is-on' : ''}`}
              onClick={() => setIndex(i)}
              title={s.label}
            >
              {s.url ? <img src={s.url} alt={s.label} /> : <span className="cs-empty" style={{ width: '100%', height: '100%' }} />}
              <span className="cs-treezoom-cell-n">{i + 1}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// Expandable trace of how a still was graded — a thumbnail after each cumulative
// stage. The compact list lives in the side panel; clicking any stage opens a
// big side-by-side inspector to scrutinize that single adjustment.
export default function EditTree({ editTree, manifest, tripId, assetId, editTreeStatus, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [inspect, setInspect] = useState(null); // stage index in the zoom inspector
  const [requesting, setRequesting] = useState(false);

  // The always-stored textual manifest (negative → final, with the parameter
  // change at each stage) renders immediately. The visual thumbnail tree is an
  // optional, on-demand enhancement — when present its stages (with image urls)
  // supersede the manifest and unlock the side-by-side inspector.
  const treeStages = editTree && Array.isArray(editTree.stages) ? editTree.stages : [];
  const manifestStages = Array.isArray(manifest) ? manifest : [];
  const stages = treeStages.length ? treeStages : manifestStages;
  const hasThumbs = stages.some((s) => s.url);
  const canBuild = !!(tripId && assetId);
  const building = requesting || editTreeStatus === 'queued' || editTreeStatus === 'building';

  const build = useCallback(async () => {
    if (!canBuild) return;
    setRequesting(true);
    try { await requestEditTree(tripId, assetId); } catch { setRequesting(false); }
  }, [canBuild, tripId, assetId]);

  // Clear the optimistic 'requesting' flag once the worker picks it up / finishes.
  useEffect(() => {
    if (editTreeStatus === 'building' || editTreeStatus === 'ready' || editTreeStatus === 'error') setRequesting(false);
  }, [editTreeStatus]);

  // No tree yet: offer to build it on demand (peels the stored negative). If we
  // don't have the ids to trigger a build, stay invisible as before.
  if (!stages.length) {
    if (!canBuild) return null;
    return (
      <div className="cs-panel" style={{ padding: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700 }}>Edit tree</span>
          <span className="cs-help" style={{ fontSize: 12, flex: 1 }}>trace every change from the master negative</span>
          <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={build} disabled={building}>
            {building ? 'Building…' : 'Build edit tree'}
          </button>
        </div>
        {editTreeStatus === 'error' && (
          <div className="cs-help" style={{ fontSize: 11, marginTop: 6, color: '#e88' }}>Couldn’t build — re-bake this still, then try again.</div>
        )}
      </div>
    );
  }

  const perception = (editTree && editTree.perception) || null;
  const regions = perception && perception.regions
    ? Object.keys(perception.regions)
      .sort((a, b) => perception.regions[b] - perception.regions[a])
      .slice(0, 4)
      .map((k) => `${k} ${Math.round(perception.regions[k] * 100)}%`)
    : [];

  // Group the ordered stages, preserving sequence within each group.
  const groups = GROUP_ORDER
    .map((g) => ({ g, items: stages.map((s, i) => ({ ...s, i })).filter((s) => s.group === g) }))
    .filter((grp) => grp.items.length);

  // Default the inspector to the first stage that actually changed something.
  const firstChanged = Math.max(0, stages.findIndex((s) => (s.changed || []).length));

  return (
    <div className="cs-panel" style={{ padding: 12 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', textAlign: 'left' }}
      >
        <span style={{ fontSize: 13, fontWeight: 700 }}>Edit tree</span>
        <span className="cs-help" style={{ fontSize: 12 }}>{stages.length} stages{hasThumbs ? '' : ' · manifest'}</span>
        <span style={{ flex: 1 }} />
        {open && canBuild && (
          <span
            role="button"
            tabIndex={0}
            className="cs-btn cs-btn-ghost cs-btn-sm"
            onClick={(e) => { e.stopPropagation(); if (!building) build(); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); if (!building) build(); } }}
            title={hasThumbs ? 'Rebuild thumbnails from the current grade (use after re-grading)' : 'Render a thumbnail for each stage (lets you compare stages side-by-side)'}
            style={{ padding: '3px 8px', marginRight: 6, opacity: building ? 0.6 : 1 }}
          >
            {building ? (hasThumbs ? 'Rebuilding…' : 'Building…') : (hasThumbs ? '↻ Rebuild' : '⊞ Add thumbnails')}
          </span>
        )}
        {open && hasThumbs && (
          <span
            role="button"
            tabIndex={0}
            className="cs-btn cs-btn-ghost cs-btn-sm"
            onClick={(e) => { e.stopPropagation(); setInspect(firstChanged); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setInspect(firstChanged); } }}
            title="Open the big side-by-side stage inspector"
            style={{ padding: '3px 8px' }}
          >
            ⤢ Compare
          </span>
        )}
        <span aria-hidden style={{ fontSize: 12 }}>{open ? '▾' : '▸'}</span>
      </button>

      {(perception?.wbRef || regions.length) && (
        <div className="cs-help" style={{ fontSize: 11, marginTop: 6, lineHeight: 1.4 }}>
          {perception?.wbRef ? <>white balance ref: <b>{perception.wbRef}</b>{regions.length ? ' · ' : ''}</> : null}
          {regions.length ? <>scene: {regions.join(', ')}</> : null}
        </div>
      )}

      {open && (
        <div style={{ marginTop: 10 }}>
          {groups.map((grp) => (
            <div key={grp.g} style={{ marginBottom: 8 }}>
              <div className="cs-help" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{GROUP_LABEL[grp.g]}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {grp.items.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => { if (hasThumbs) setInspect(s.i); }}
                    title={hasThumbs ? 'Open big side-by-side' : 'Add thumbnails to compare stages'}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', background: 'none', border: '1px solid var(--border, #2a2a2a)', borderRadius: 6, padding: 4, cursor: hasThumbs ? 'pointer' : 'default', color: 'inherit', textAlign: 'left' }}
                  >
                    {hasThumbs && (
                      <span style={{ flex: '0 0 auto', width: 64, height: 36, borderRadius: 4, overflow: 'hidden', background: '#111' }}>
                        {s.url ? <img src={s.url} alt={s.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
                      </span>
                    )}
                    <span style={{ minWidth: 0, flex: 1 }}>
                      <span style={{ fontSize: 12, fontWeight: 600 }}>{s.label}</span>
                      {(s.changed || []).length ? (
                        <span className="cs-help" style={{ display: 'block', fontSize: 10, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {s.changed.map(chipText).join(' · ')}
                        </span>
                      ) : (
                        <span className="cs-help" style={{ display: 'block', fontSize: 10, lineHeight: 1.3, opacity: 0.6 }}>carries previous forward</span>
                      )}
                    </span>
                    {hasThumbs && <span aria-hidden className="cs-help" style={{ fontSize: 13 }}>⤢</span>}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {inspect != null && (
        <StageInspector
          stages={stages}
          index={inspect}
          setIndex={(updater) => setInspect((i) => (typeof updater === 'function' ? updater(i) : updater))}
          onClose={() => setInspect(null)}
          perception={perception}
        />
      )}
    </div>
  );
}
